import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { hrmAPI } from '../../lib/api';
import { 
    ArrowLeft, User, FileText, Phone, Briefcase, GraduationCap,
    Plus, Edit, Trash2, X, Save, File, Image, Eye
} from 'lucide-react';

// Storage URL for files - points to Laravel backend
const STORAGE_URL = process.env.REACT_APP_STORAGE_URL || 'http://localhost:8000/storage';

const EmployeeProfile = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [employee, setEmployee] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('documents');
    
    // Documents state
    const [documents, setDocuments] = useState([]);
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [documentForm, setDocumentForm] = useState({
        document_type: '',
        document_number: '',
        issue_date: '',
        expiry_date: '',
        file: null,
        previewUrl: null
    });
    const [editingDocumentId, setEditingDocumentId] = useState(null);

    // Emergency Contacts state
    const [emergencyContacts, setEmergencyContacts] = useState([]);
    const [showContactModal, setShowContactModal] = useState(false);
    const [contactForm, setContactForm] = useState({
        name: '',
        relationship: '',
        phone: '',
        email: '',
        address: ''
    });
    const [editingContactId, setEditingContactId] = useState(null);

    // Experiences state
    const [experiences, setExperiences] = useState([]);
    const [showExperienceModal, setShowExperienceModal] = useState(false);
    const [experienceForm, setExperienceForm] = useState({
        company_name: '',
        company_address: '',
        designation: '',
        department: '',
        from_date: '',
        to_date: '',
        is_current: false,
        description: ''
    });
    const [editingExperienceId, setEditingExperienceId] = useState(null);

    // Qualifications state
    const [qualifications, setQualifications] = useState([]);
    const [showQualificationModal, setShowQualificationModal] = useState(false);
    const [qualificationForm, setQualificationForm] = useState({
        degree: '',
        institute: '',
        field_of_study: '',
        year_of_passing: '',
        grade: '',
        description: ''
    });
    const [editingQualificationId, setEditingQualificationId] = useState(null);

    useEffect(() => {
        fetchEmployee();
    }, [id]);

    const fetchEmployee = async () => {
        try {
            setLoading(true);
            const response = await hrmAPI.getEmployee(id);
            const employeeData = response.data.data || response.data;
            setEmployee(employeeData);
            setDocuments(employeeData.documents || []);
            setEmergencyContacts(employeeData.emergency_contacts || []);
            setExperiences(employeeData.experiences || []);
            setQualifications(employeeData.qualifications || []);
        } catch (err) {
            setError('Failed to load employee data');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Document handlers
    const handleDocumentSubmit = async (e) => {
        e.preventDefault();
        try {
            const formData = new FormData();
            formData.append('employee_id', id);
            formData.append('document_type', documentForm.document_type);
            formData.append('document_number', documentForm.document_number);
            formData.append('issue_date', documentForm.issue_date);
            formData.append('expiry_date', documentForm.expiry_date);
            if (documentForm.file) {
                formData.append('file', documentForm.file);
            }

            if (editingDocumentId) {
                await hrmAPI.updateEmployeeDocument(editingDocumentId, formData);
            } else {
                await hrmAPI.createEmployeeDocument(formData);
            }
            setShowDocumentModal(false);
            resetDocumentForm();
            fetchEmployee();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save document');
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Create preview URL
            const previewUrl = URL.createObjectURL(file);
            setDocumentForm({
                ...documentForm,
                file: file,
                previewUrl: previewUrl
            });
        }
    };

    const resetDocumentForm = () => {
        setDocumentForm({
            document_type: '',
            document_number: '',
            issue_date: '',
            expiry_date: '',
            file: null,
            previewUrl: null
        });
        setEditingDocumentId(null);
    };

    const handleEditDocument = (doc) => {
        setDocumentForm({
            document_type: doc.document_type || '',
            document_number: doc.document_number || '',
            issue_date: doc.issue_date || '',
            expiry_date: doc.expiry_date || '',
            file: null,
            previewUrl: doc.document_path ? `${STORAGE_URL}/${doc.document_path}` : null
        });
        setEditingDocumentId(doc.id);
        setShowDocumentModal(true);
    };

    const handleDeleteDocument = async (docId) => {
        if (!window.confirm('Are you sure you want to delete this document?')) return;
        try {
            await hrmAPI.deleteEmployeeDocument(docId);
            fetchEmployee();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete document');
        }
    };

    // Emergency Contact handlers
    const handleContactSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = {
                employee_id: id,
                ...contactForm
            };
            if (editingContactId) {
                await hrmAPI.updateEmployeeEmergencyContact(editingContactId, data);
            } else {
                await hrmAPI.createEmployeeEmergencyContact(data);
            }
            setShowContactModal(false);
            resetContactForm();
            fetchEmployee();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save contact');
        }
    };

    const resetContactForm = () => {
        setContactForm({
            name: '',
            relationship: '',
            phone: '',
            email: '',
            address: ''
        });
        setEditingContactId(null);
    };

    const handleEditContact = (contact) => {
        setContactForm({
            name: contact.name || '',
            relationship: contact.relationship || '',
            phone: contact.phone || '',
            email: contact.email || '',
            address: contact.address || ''
        });
        setEditingContactId(contact.id);
        setShowContactModal(true);
    };

    const handleDeleteContact = async (contactId) => {
        if (!window.confirm('Are you sure you want to delete this contact?')) return;
        try {
            await hrmAPI.deleteEmployeeEmergencyContact(contactId);
            fetchEmployee();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete contact');
        }
    };

    // Experience handlers
    const handleExperienceSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = {
                employee_id: id,
                ...experienceForm
            };
            if (editingExperienceId) {
                await hrmAPI.updateEmployeeExperience(editingExperienceId, data);
            } else {
                await hrmAPI.createEmployeeExperience(data);
            }
            setShowExperienceModal(false);
            resetExperienceForm();
            fetchEmployee();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save experience');
        }
    };

    const resetExperienceForm = () => {
        setExperienceForm({
            company_name: '',
            company_address: '',
            designation: '',
            department: '',
            from_date: '',
            to_date: '',
            is_current: false,
            description: ''
        });
        setEditingExperienceId(null);
    };

    const handleEditExperience = (exp) => {
        setExperienceForm({
            company_name: exp.company_name || '',
            company_address: exp.company_address || '',
            designation: exp.designation || '',
            department: exp.department || '',
            from_date: exp.from_date || '',
            to_date: exp.to_date || '',
            is_current: exp.is_current || false,
            description: exp.description || ''
        });
        setEditingExperienceId(exp.id);
        setShowExperienceModal(true);
    };

    const handleDeleteExperience = async (expId) => {
        if (!window.confirm('Are you sure you want to delete this experience?')) return;
        try {
            await hrmAPI.deleteEmployeeExperience(expId);
            fetchEmployee();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete experience');
        }
    };

    // Qualification handlers
    const handleQualificationSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = {
                employee_id: id,
                ...qualificationForm
            };
            if (editingQualificationId) {
                await hrmAPI.updateEmployeeQualification(editingQualificationId, data);
            } else {
                await hrmAPI.createEmployeeQualification(data);
            }
            setShowQualificationModal(false);
            resetQualificationForm();
            fetchEmployee();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save qualification');
        }
    };

    const resetQualificationForm = () => {
        setQualificationForm({
            degree: '',
            institute: '',
            field_of_study: '',
            year_of_passing: '',
            grade: '',
            description: ''
        });
        setEditingQualificationId(null);
    };

    const handleEditQualification = (qual) => {
        setQualificationForm({
            degree: qual.degree || '',
            institute: qual.institute || '',
            field_of_study: qual.field_of_study || '',
            year_of_passing: qual.year_of_passing || '',
            grade: qual.grade || '',
            description: qual.description || ''
        });
        setEditingQualificationId(qual.id);
        setShowQualificationModal(true);
    };

    const handleDeleteQualification = async (qualId) => {
        if (!window.confirm('Are you sure you want to delete this qualification?')) return;
        try {
            await hrmAPI.deleteEmployeeQualification(qualId);
            fetchEmployee();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete qualification');
        }
    };

    const tabs = [
        { id: 'documents', label: 'Documents', icon: FileText },
        { id: 'emergency_contacts', label: 'Emergency Contacts', icon: Phone },
        { id: 'experiences', label: 'Experiences', icon: Briefcase },
        { id: 'qualifications', label: 'Qualifications', icon: GraduationCap }
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    if (!employee) {
        return (
            <div className="text-center py-8">
                <p className="text-red-600">Employee not found</p>
                <button onClick={() => navigate('/hrm/employees')} className="text-indigo-600 hover:underline mt-2">
                    Back to Employees
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    <button 
                        onClick={() => navigate('/hrm/employees')}
                        className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                            {employee.name}
                        </h1>
                        <p className="text-gray-500 dark:text-gray-400">
                            {employee.designation?.name} - {employee.department?.name}
                        </p>
                    </div>
                </div>
            </div>

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                    {error}
                </div>
            )}

            {/* Tabs */}
            <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="flex space-x-8">
                    {tabs.map((tab) => {
                        const TabIcon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`
                                    flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors
                                    ${activeTab === tab.id
                                        ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                    }
                                `}
                            >
                                <TabIcon className="w-4 h-4 mr-2" />
                                {tab.label}
                                <span className="ml-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 py-0.5 px-2 rounded-full text-xs">
                                    {tab.id === 'documents' && documents.length}
                                    {tab.id === 'emergency_contacts' && emergencyContacts.length}
                                    {tab.id === 'experiences' && experiences.length}
                                    {tab.id === 'qualifications' && qualifications.length}
                                </span>
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* Documents Tab */}
            {activeTab === 'documents' && (
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h2 className="text-lg font-semibold">Documents</h2>
                        <button
                            onClick={() => { resetDocumentForm(); setShowDocumentModal(true); }}
                            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Document
                        </button>
                    </div>

                    {documents.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                            No documents found. Click "Add Document" to add one.
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {documents.map((doc) => (
                                <div key={doc.id} className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
                                    {doc.document_path ? (
                                        <div className="h-32 bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                                            {doc.document_path.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                                                <img 
                                                    src={`${STORAGE_URL}/${doc.document_path}`} 
                                                    alt={doc.document_type}
                                                    className="h-full w-auto object-cover"
                                                />
                                            ) : (
                                                <FileText className="w-12 h-12 text-gray-400" />
                                            )}
                                        </div>
                                    ) : (
                                        <div className="h-32 bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                                            <FileText className="w-12 h-12 text-gray-400" />
                                        </div>
                                    )}
                                    <div className="p-3">
                                        <h3 className="font-medium text-gray-900 dark:text-white text-sm truncate">{doc.document_type}</h3>
                                        <p className="text-xs text-gray-500 truncate">{doc.document_number}</p>
                                        <div className="flex justify-between items-center mt-2">
                                            {doc.document_path && (
                                                <a 
                                                    href={`${STORAGE_URL}/${doc.document_path}`} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="text-indigo-600 hover:text-indigo-900"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </a>
                                            )}
                                            <div className="flex space-x-2 ml-auto">
                                                <button onClick={() => handleEditDocument(doc)} className="text-indigo-600 hover:text-indigo-900">
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => handleDeleteDocument(doc.id)} className="text-red-600 hover:text-red-900">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Emergency Contacts Tab */}
            {activeTab === 'emergency_contacts' && (
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h2 className="text-lg font-semibold">Emergency Contacts</h2>
                        <button
                            onClick={() => { resetContactForm(); setShowContactModal(true); }}
                            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Contact
                        </button>
                    </div>

                    {emergencyContacts.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                            No emergency contacts found. Click "Add Contact" to add one.
                        </div>
                    ) : (
                        <div className="grid gap-4">
                            {emergencyContacts.map((contact) => (
                                <div key={contact.id} className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h3 className="font-medium text-gray-900 dark:text-white">{contact.name}</h3>
                                            <p className="text-sm text-gray-500">Relationship: {contact.relationship}</p>
                                            <p className="text-sm text-gray-500">Phone: {contact.phone}</p>
                                            {contact.email && <p className="text-sm text-gray-500">Email: {contact.email}</p>}
                                            {contact.address && <p className="text-sm text-gray-500">Address: {contact.address}</p>}
                                        </div>
                                        <div className="flex space-x-2">
                                            <button onClick={() => handleEditContact(contact)} className="text-indigo-600 hover:text-indigo-900">
                                                <Edit className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => handleDeleteContact(contact.id)} className="text-red-600 hover:text-red-900">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Experiences Tab */}
            {activeTab === 'experiences' && (
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h2 className="text-lg font-semibold">Work Experiences</h2>
                        <button
                            onClick={() => { resetExperienceForm(); setShowExperienceModal(true); }}
                            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Experience
                        </button>
                    </div>

                    {experiences.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                            No work experiences found. Click "Add Experience" to add one.
                        </div>
                    ) : (
                        <div className="grid gap-4">
                            {experiences.map((exp) => (
                                <div key={exp.id} className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h3 className="font-medium text-gray-900 dark:text-white">{exp.designation}</h3>
                                            <p className="text-sm text-gray-500">{exp.company_name}</p>
                                            {exp.company_address && <p className="text-sm text-gray-500">{exp.company_address}</p>}
                                            {exp.department && <p className="text-sm text-gray-500">Department: {exp.department}</p>}

                                            <p className="text-sm text-gray-500">
                                                {exp.from_date && new Date(exp.from_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} - {exp.is_current  ? 'Present' : exp.to_date && new Date(exp.to_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                            </p>
                                        </div>
                                        <div className="flex space-x-2">
                                            <button onClick={() => handleEditExperience(exp)} className="text-indigo-600 hover:text-indigo-900">
                                                <Edit className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => handleDeleteExperience(exp.id)} className="text-red-600 hover:text-red-900">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Qualifications Tab */}
            {activeTab === 'qualifications' && (
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h2 className="text-lg font-semibold">Qualifications</h2>
                        <button
                            onClick={() => { resetQualificationForm(); setShowQualificationModal(true); }}
                            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Qualification
                        </button>
                    </div>

                    {qualifications.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                            No qualifications found. Click "Add Qualification" to add one.
                        </div>
                    ) : (
                        <div className="grid gap-4">
                            {qualifications.map((qual) => (
                                <div key={qual.id} className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h3 className="font-medium text-gray-900 dark:text-white">{qual.degree}</h3>
                                            {qual.field_of_study && <p className="text-sm text-gray-500">{qual.field_of_study}</p>}
                                            <p className="text-sm text-gray-500">{qual.institute}</p>
                                            <p className="text-sm text-gray-500">
                                                {qual.year_of_passing} {qual.grade && `| Grade: ${qual.grade}`}
                                            </p>
                                            {qual.description && <p className="text-sm text-gray-500 mt-1">{qual.description}</p>}
                                        </div>
                                        <div className="flex space-x-2">
                                            <button onClick={() => handleEditQualification(qual)} className="text-indigo-600 hover:text-indigo-900">
                                                <Edit className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => handleDeleteQualification(qual.id)} className="text-red-600 hover:text-red-900">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Document Modal */}
            {showDocumentModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">
                                {editingDocumentId ? 'Edit Document' : 'Add Document'}
                            </h3>
                            <button onClick={() => setShowDocumentModal(false)} className="text-gray-500 hover:text-gray-700">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleDocumentSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Document Type</label>
                                <select
                                    value={documentForm.document_type}
                                    onChange={(e) => setDocumentForm({...documentForm, document_type: e.target.value})}
                                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2 border"
                                    required
                                >
                                    <option value="">Select Type</option>
                                    <option value="NID">NID</option>
                                    <option value="Passport">Passport</option>
                                    <option value="Driving License">Driving License</option>
                                    <option value="Birth Certificate">Birth Certificate</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Document Number</label>
                                <input
                                    type="text"
                                    value={documentForm.document_number}
                                    onChange={(e) => setDocumentForm({...documentForm, document_number: e.target.value})}
                                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2 border"
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Issue Date</label>
                                    <input
                                        type="date"
                                        value={documentForm.issue_date}
                                        onChange={(e) => setDocumentForm({...documentForm, issue_date: e.target.value})}
                                        className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2 border"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Expiry Date</label>
                                    <input
                                        type="date"
                                        value={documentForm.expiry_date}
                                        onChange={(e) => setDocumentForm({...documentForm, expiry_date: e.target.value})}
                                        className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2 border"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">File (Image/PDF)</label>
                                <input
                                    type="file"
                                    accept="image/*,.pdf"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            setDocumentForm({
                                                ...documentForm,
                                                file: file,
                                                previewUrl: URL.createObjectURL(file)
                                            });
                                        }
                                    }}
                                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2 border"
                                />
                                {(documentForm.previewUrl || (editingDocumentId && documents.find(d => d.id === editingDocumentId)?.document_path)) && (
                                    <div className="mt-2">
                                        {documentForm.previewUrl ? (
                                            <img src={documentForm.previewUrl} alt="Preview" className="h-32 w-auto rounded" />
                                        ) : (
                                            <div className="flex items-center text-sm text-green-600">
                                                <Image className="w-4 h-4 mr-2" />
                                                File already uploaded
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setShowDocumentModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                                >
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Contact Modal */}
            {showContactModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">
                                {editingContactId ? 'Edit Contact' : 'Add Emergency Contact'}
                            </h3>
                            <button onClick={() => setShowContactModal(false)} className="text-gray-500 hover:text-gray-700">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleContactSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                                <input
                                    type="text"
                                    value={contactForm.name}
                                    onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2 border"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Relationship</label>
                                <input
                                    type="text"
                                    value={contactForm.relationship}
                                    onChange={(e) => setContactForm({...contactForm, relationship: e.target.value})}
                                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2 border"
                                    placeholder="e.g., Spouse, Father, Brother"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Phone</label>
                                <input
                                    type="text"
                                    value={contactForm.phone}
                                    onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2 border"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email (Optional)</label>
                                <input
                                    type="email"
                                    value={contactForm.email}
                                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2 border"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Address (Optional)</label>
                                <textarea
                                    value={contactForm.address}
                                    onChange={(e) => setContactForm({...contactForm, address: e.target.value})}
                                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2 border"
                                    rows="2"
                                />
                            </div>
                            <div className="flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setShowContactModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                                >
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Experience Modal */}
            {showExperienceModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">
                                {editingExperienceId ? 'Edit Experience' : 'Add Work Experience'}
                            </h3>
                            <button onClick={() => setShowExperienceModal(false)} className="text-gray-500 hover:text-gray-700">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleExperienceSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Company Name</label>
                                <input
                                    type="text"
                                    value={experienceForm.company_name}
                                    onChange={(e) => setExperienceForm({...experienceForm, company_name: e.target.value})}
                                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2 border"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Company Address</label>
                                <textarea
                                    value={experienceForm.company_address}
                                    onChange={(e) => setExperienceForm({...experienceForm, company_address: e.target.value})}
                                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2 border"
                                    rows="2"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Designation</label>
                                <input
                                    type="text"
                                    value={experienceForm.designation}
                                    onChange={(e) => setExperienceForm({...experienceForm, designation: e.target.value})}
                                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2 border"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Department</label>
                                <input
                                    type="text"
                                    value={experienceForm.department}
                                    onChange={(e) => setExperienceForm({...experienceForm, department: e.target.value})}
                                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2 border"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">From Date</label>
                                    <input
                                        type="date"
                                        value={experienceForm.from_date}
                                        onChange={(e) => setExperienceForm({...experienceForm, from_date: e.target.value})}
                                        className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2 border"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">To Date</label>
                                    <input
                                        type="date"
                                        value={experienceForm.to_date}
                                        onChange={(e) => setExperienceForm({...experienceForm, to_date: e.target.value})}
                                        className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2 border"
                                        disabled={experienceForm.is_current}
                                    />
                                </div>
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="is_current"
                                    checked={experienceForm.is_current}
                                    onChange={(e) => setExperienceForm({
                                        ...experienceForm, 
                                        is_current: e.target.checked,
                                        to_date: e.target.checked ? '' : experienceForm.to_date
                                    })}
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                />
                                <label htmlFor="is_current" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                    I currently work here
                                </label>
                            </div>
                            <div className="flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setShowExperienceModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                                >
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Qualification Modal */}
            {showQualificationModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">
                                {editingQualificationId ? 'Edit Qualification' : 'Add Qualification'}
                            </h3>
                            <button onClick={() => setShowQualificationModal(false)} className="text-gray-500 hover:text-gray-700">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleQualificationSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Institute</label>
                                <input
                                    type="text"
                                    value={qualificationForm.institute}
                                    onChange={(e) => setQualificationForm({...qualificationForm, institute: e.target.value})}
                                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2 border"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Degree</label>
                                <input
                                    type="text"
                                    value={qualificationForm.degree}
                                    onChange={(e) => setQualificationForm({...qualificationForm, degree: e.target.value})}
                                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2 border"
                                    placeholder="e.g., BSc, MSc, Diploma"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Field of Study</label>
                                <input
                                    type="text"
                                    value={qualificationForm.field_of_study}
                                    onChange={(e) => setQualificationForm({...qualificationForm, field_of_study: e.target.value})}
                                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2 border"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Year of Passing</label>
                                <input
                                    type="text"
                                    value={qualificationForm.year_of_passing}
                                    onChange={(e) => setQualificationForm({...qualificationForm, year_of_passing: e.target.value})}
                                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2 border"
                                    placeholder="e.g., 2020"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Grade/GPA</label>
                                <input
                                    type="text"
                                    value={qualificationForm.grade}
                                    onChange={(e) => setQualificationForm({...qualificationForm, grade: e.target.value})}
                                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2 border"
                                    placeholder="e.g., A, 3.5/4.0"
                                />
                            </div>
                            <div className="flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setShowQualificationModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                                >
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeeProfile;
